import _ from 'lodash-es';
import { FeatureId } from '@/react/portainer/feature-flags/enums';
const DEFAULT_GROUP_FILTER = '(objectClass=group)';
import { isLimitedToBE } from '@/react/portainer/feature-flags/feature-flags.service';
import { readFileAsText } from '@/portainer/services/fileUploadReact';
import { getPublicSettings } from '@/react/portainer/settings/settings.service';

const bindingOptions = [
  { label: 'Simple', value: 'simple' },
  { label: 'Kerberos', value: 'kerberos' },
];

const serviceOptions = [
  { label: 'LDAP', value: 'ldap' },
  { label: 'HOST', value: 'host' },
];

export default class AdSettingsController {
  /* @ngInject */
  constructor(LDAPService, $async, $scope) {
    this.LDAPService = LDAPService;
    this.$async = $async;
    this.$scope = $scope;

    this.domainSuffix = '';
    this.limitedFeatureId = FeatureId.HIDE_INTERNAL_AUTH;
    this.onTlscaCertChange = this.onTlscaCertChange.bind(this);
    this.searchUsers = this.searchUsers.bind(this);
    this.searchGroups = this.searchGroups.bind(this);
    this.searchAdminGroups = this.searchAdminGroups.bind(this);
    this.parseDomainName = this.parseDomainName.bind(this);
    this.onAccountChange = this.onAccountChange.bind(this);
    this.onAccountChangeIfEmpty = this.onAccountChangeIfEmpty.bind(this);
    this.onBindingChange = this.onBindingChange.bind(this);
    this.onKerberosConfigChange = this.onKerberosConfigChange.bind(this);
    this.onAutoUserProvisionChange = this.onAutoUserProvisionChange.bind(this);
  }

  onAutoUserProvisionChange(value) {
    this.$scope.$evalAsync(() => {
      this.settings.AutoCreateUsers = value;
    });
  }

  parseDomainName(account) {
    this.domainName = '';

    if (!account || !account.includes('@')) {
      this.domainSuffix = '';
      return;
    }

    const [, domainName] = account.split('@');
    if (!domainName) {
      // When domainName does not exist anymore, we should clean up the
      // domainSuffix to overwrite the previous domainSuffix.
      // The issue happens when deleting the character with Backspace one by one
      this.domainSuffix = '';
      return;
    }

    const parts = _.compact(domainName.split('.'));
    this.domainSuffix = parts.map((part) => `dc=${part}`).join(',');
  }

  parseKerberosRealm(realm) {
    if (!realm) {
      this.domainSuffix = '';
      return;
    }

    const parts = _.compact(realm.toLowerCase().split('.'));
    this.domainSuffix = parts.map((part) => `dc=${part}`).join(',');
  }

  onAccountChange(account) {
    this.parseDomainName(account);
  }

  // ng-keyup event
  // This is helpful when user select all content and click Backspace to empty the field
  onAccountChangeIfEmpty(account) {
    if (account === '') {
      this.parseDomainName(account);
    }
  }

  onBindingChange(binding) {
    if (binding === 'kerberos' && !this.settings.Kerberos.Service) {
      this.settings.Kerberos.Service = 'ldap';
    }

    if (binding === 'kerberos') {
      this.parseKerberosRealm(this.settings.Kerberos.Realm);
    } else {
      this.parseDomainName(this.settings.ReaderDN);
    }
  }

  onKerberosRealmChange(realm) {
    if (realm) {
      this.parseKerberosRealm(realm.toLowerCase());
    }
  }

  onKerberosConfigChange(file) {
    return this.$async(async () => {
      const maxFileSize = 10 * 1024 * 1024; // 10MB
      const fileText = await readFileAsText(file, maxFileSize);
      this.settings.Kerberos.Configuration = fileText;
      this.state.KerberosFile = file;
    });
  }

  searchUsers() {
    return this.LDAPService.users(this.settings);
  }

  searchGroups() {
    return this.LDAPService.groups(this.settings);
  }

  searchAdminGroups() {
    if (this.settings.AdminAutoPopulate) {
      this.settings.AdminGroups = this.selectedAdminGroups;
    }

    const settings = {
      ...this.settings,
      AdminGroupSearchSettings: this.settings.AdminGroupSearchSettings.map((search) => ({ ...search, GroupFilter: search.GroupFilter || DEFAULT_GROUP_FILTER })),
    };

    return this.LDAPService.adminGroups(settings);
  }

  onTlscaCertChange(file) {
    this.tlscaCert = file;
  }

  addLDAPUrl() {
    this.settings.URLs.push('');
  }

  removeLDAPUrl(index) {
    this.settings.URLs.splice(index, 1);
  }

  isSaveSettingButtonDisabled() {
    return isLimitedToBE(this.limitedFeatureId) || !this.isLdapFormValid();
  }

  $onInit() {
    this.$async(async () => {
      const publicSettings = await getPublicSettings();
      const isFIPSMode = !!publicSettings && publicSettings.FIPSMode;
      bindingOptions[1] = { ...bindingOptions[1], disabled: isFIPSMode };
    });

    this.tlscaCert = this.settings.TLSCACert;
    this.parseDomainName(this.settings.ReaderDN);
    this.state.BindingOptions = bindingOptions;
    this.state.ServiceOptions = serviceOptions;
    this.state.KerberosFile = undefined;
    this.settings.BindType = this.settings.Kerberos.Realm ? 'kerberos' : 'simple';
  }
}
